import type { AppRouter } from "@gestiondoc/api/router";
import { QueryClient } from "@tanstack/react-query";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import { createTRPCContext, createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import { twoFactorClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import superjson from "superjson";
import { mensajeDe } from "./utiles.ts";

/**
 * Cliente de sesión (better-auth). Mismo origen: /api/auth pasa por el proxy. El plugin del
 * segundo factor (fase 6) solo aporta `authClient.twoFactor.*`: cuándo se pide el código lo
 * decide la API por sesión y origen, y la app lo lee en `yo.perfil.segundoFactor`.
 */
export const authClient = createAuthClient({
  basePath: "/api/auth",
  plugins: [twoFactorClient()],
});

/**
 * El único caché de consultas de la app. Tras una mutación se llama `queryClient.invalidateQueries()`
 * sin clave, a propósito: casi todo lo que se ve depende de lo que acaba de cambiar (la lista, el
 * detalle, la bandeja, el contador del menú) y recargarlo todo es más barato que olvidarse de una
 * clave. Las pantallas lo importan de aquí en vez de `useQueryClient()`.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: (n, e) => !esNoAutorizado(e) && n < 2 },
  },
});

/**
 * Opciones de una mutación que avisa en pantalla: si sale bien, recarga todo, limpia el aviso y
 * hace lo que siga; si falla, muestra el mensaje.
 */
export function conAviso(onAviso: (mensaje: string | null) => void, alTerminar?: () => void) {
  return {
    onSuccess: () => {
      queryClient.invalidateQueries();
      onAviso(null);
      alTerminar?.();
    },
    onError: (e: unknown) => onAviso(mensajeDe(e)),
  };
}

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: "/trpc",
      transformer: superjson,
      headers: () => ({ "x-gestiondoc-canal": esMovil() ? "movil" : "web" }),
    }),
  ],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({ client: trpcClient, queryClient });

export const { TRPCProvider, useTRPC } = createTRPCContext<AppRouter>();

export function esNoAutorizado(e: unknown): boolean {
  return (
    typeof e === "object" &&
    e !== null &&
    "data" in e &&
    (e as { data?: { code?: string } }).data?.code === "UNAUTHORIZED"
  );
}

function esMovil(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;
}
